import FormatCurrency from 'components/Common/FormatCurrency'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useRef, useMemo } from 'react'
import * as React from 'react'
import TextLink from 'LuxKit/TextLink'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { OfferFilterSelectButton, OfferFilterSelectButtonContent } from '../OfferFilterSelectButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Switch from 'LuxKit/Switch/Switch'
import OfferAirportSelectDropdown from '../Airport/OfferAirportSelectDropdown'
import styled from 'styled-components'
import NestableClickable from 'components/Common/NestableClickable'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import { useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import type { FlightToggleDisplayPrice } from 'components/OfferPage/FlightDeal/useOfferPageFlightToggleDisplayPrice'
import { rem } from 'polished'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsEnabledFlightsBundledLuxPlusPricing, getIsFlightsLuxPlusPreviewGateOpen } from 'luxPlus/selectors/featureToggle'

const DropdownContainer = styled.div`
  position: relative;
`

const MemberDiscountSelectButton = styled(OfferFilterSelectButton)`
  &.has-member-discount {
    box-shadow: inset 0 0 0 2px ${props => props.theme.palette.product.luxPlus.background};
  }
`

const MemberDiscountPill = styled.div`
  position: absolute;
  top: ${rem(-12)};
  inset-inline-start: ${rem(12)};
  z-index: 2;
  pointer-events: none;
`

interface Props {
  className?: string
  rooms: Array<OfferPageRoom>
  activeFilter?: string
  flights?: boolean
  airport?: App.Airport
  totalFlightPrice?: number
  displayFlightPrice?: FlightToggleDisplayPrice
  checkInDate?: string
  checkOutDate?: string
  bundledWithFlightsOnly?: boolean
  isAuDomesticFlight?: boolean
}

function OfferDesktopFlightToggle({
  className,
  rooms,
  activeFilter,
  flights,
  airport,
  totalFlightPrice,
  displayFlightPrice,
  checkInDate,
  checkOutDate,
  bundledWithFlightsOnly,
  isAuDomesticFlight,
}: Props) {
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isOpen = activeFilter === 'airport'
  const trigger = useRef<HTMLButtonElement>(null)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const previewGateOpen = useAppSelector(getIsFlightsLuxPlusPreviewGateOpen)
  const memberDiscountAvailable = useAppSelector(getIsEnabledFlightsBundledLuxPlusPricing) && canViewLuxPlusBenefits && !bundledWithFlightsOnly && previewGateOpen
  const showMemberDiscountBorder = memberDiscountAvailable && !isAuDomesticFlight

  const onButtonClick = useCallback(() => {
    if (bundledWithFlightsOnly) {
      // When bundledWithFlightsOnly, toggle the airport dropdown
      if (isOpen) {
        dispatch({ type: OfferPageActions.closeFilter })
        trackEvent('AIRPORT_FILTER_CLOSED')
      } else {
        dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
        trackEvent('AIRPORT_FILTER_PRESSED')
      }
    } else {
      // Default behavior: toggle flights on/off
      if (isOpen) {
        dispatch({ type: OfferPageActions.closeFilter })
        trackEvent('AIRPORT_FILTER_CLOSED')
      } else {
        dispatch({ type: OfferPageActions.setFlights, flights: !flights })
      }
    }
  }, [bundledWithFlightsOnly, dispatch, flights, isOpen, trackEvent])

  const onAirportNameClick = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.stopPropagation()
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('AIRPORT_FILTER_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'airport' })
      trackEvent('AIRPORT_FILTER_PRESSED')
    }
  }, [isOpen, dispatch, trackEvent])

  const closeFilter = useCallback(() => {
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('AIRPORT_FILTER_CLOSED')
  }, [dispatch, trackEvent])

  const onAirportSelect = useCallback(() => {
    trackEvent('AIRPORT_FILTER_SELECTED')
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, trackEvent])

  const flightPricePerPerson = useMemo(() => {
    if (!totalFlightPrice) {
      return undefined
    }
    const flightOccupants = countOccupantsForFlights(rooms)
    const totalPeople = flightOccupants.adults + flightOccupants.children + flightOccupants.infants
    return totalFlightPrice / totalPeople
  }, [totalFlightPrice, rooms])
  const flightPriceUnit = checkInDate && checkOutDate ? 'person' : 'adult'
  const toggleFlightPrice = displayFlightPrice?.amount ?? flightPricePerPerson
  const toggleFlightPriceUnit = displayFlightPrice?.unit ?? flightPriceUnit

  const bundleWithFlightsLabel = useMemo(() => {
    return airport
      ? intl.formatMessage(
          {
            id: 'JM06LhuinqWB3lT4KrTg',
            defaultMessage: 'Select flights from {airportCode}',
            description: 'Label for selecting flights from a specific airport',
          },
          { airportCode: airport.code },
        )
      : intl.formatMessage({
          id: 'mIQv4pD0Q9dzcfOplk1F',
          defaultMessage: 'Select flights',
          description: 'Section label for the flight selection step in the package booking configuration',
        })
  }, [airport, intl])

  const addFlightsText = useMemo(() => {
    return airport
      ? intl.formatMessage(
          {
            id: 'wJ1quNrpdYSkEZMJTruF',
            defaultMessage: 'Add a flight from <link>{airportCode}</link>',
            description: 'Descriptive text for adding flights from a specific airport',
          },
          {
            link: (chunks: React.ReactNode) => (
              <TextLink onClick={onAirportNameClick} data-testid="flights-select-airport" key="airport-link">
                {chunks}
              </TextLink>
            ),
            airportCode: airport.code,
          },
        )
      : intl.formatMessage(commonMessages.addFlights)
  }, [airport, intl, onAirportNameClick])

  return <DropdownContainer className={className}>
    {bundledWithFlightsOnly && <OfferFilterDropdownBox
      onClick={onButtonClick}
      ref={trigger}
      active={isOpen}
      data-testid="flights-toggle"
      icon={<LinePlaneDepartureIcon />}
      label={intl.formatMessage({
        id: '4WhgOTiVtrnyIzOyJk2j',
        defaultMessage: 'Flights',
        description: 'Filter label for the flights category option in the search results filter panel',
      })}
      placeholder={bundleWithFlightsLabel}
      value={flightPricePerPerson && (
        <b>{bundleWithFlightsLabel}</b>
      )}
    />}
    {!bundledWithFlightsOnly && <>
      <MemberDiscountSelectButton
        onClick={onButtonClick}
        ref={trigger}
        as={NestableClickable}
        data-testid="flights-toggle"
        data-dropdown-keep-open
        className={showMemberDiscountBorder ? 'has-member-discount' : undefined}
      >
        <LinePlaneDepartureIcon />
        <OfferFilterSelectButtonContent>
          <BodyText variant="medium" wrap="truncate">
            {addFlightsText}
          </BodyText>
          {!!toggleFlightPrice && <Caption variant="medium" colour="light-foreground-alt" wrap="truncate">
            {intl.formatMessage(
              {
                id: 'PC1cdif3TQsIttlpFI56',
                defaultMessage: 'From {price}/{unit}',
                description: 'Price display showing the starting price per adult for flight search result cards',
              },
              {
                price: <FormatCurrency value={toggleFlightPrice} format="rounded" />,
                unit: toggleFlightPriceUnit,
              },
            )}
          </Caption>}
        </OfferFilterSelectButtonContent>
        <Switch
          checked={flights}
          onChange={onButtonClick}
        />
      </MemberDiscountSelectButton>
      {showMemberDiscountBorder && <MemberDiscountPill>
        <LuxPlusLabel type="member-price-offer-flight" />
      </MemberDiscountPill>}
    </>}
    <OfferAirportSelectDropdown
      open={isOpen}
      anchorRef={trigger}
      triggerRef={trigger}
      onClose={closeFilter}
      onSelectAirport={onAirportSelect}
      selectedAirportCode={airport?.code}
    />
  </DropdownContainer>
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'airport'
  | 'flights'
  | 'activeFilter'
  | 'checkInDate'
  | 'checkOutDate'
>

export default withOfferPageState((state): MappedProps => ({
  rooms: state.rooms,
  activeFilter: state.activeFilter,
  airport: state.airport,
  flights: state.flights,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
}), OfferDesktopFlightToggle)
